/**
 * Schema.org JSON-LD builders for SEO rich snippets.
 *
 * All builders return plain objects; the JsonLd component wraps them in
 * <script type="application/ld+json"> and injects to <head>.
 */

const SITE_URL = process.env.NEXT_PUBLIC_SITE_URL || 'https://www.leadhub.co';
const SITE_NAME = 'Leadhub';

// Stable @id references so schemas can link to each other instead of duplicating data
const ORG_ID = `${SITE_URL}/#organization`;
const WEBSITE_ID = `${SITE_URL}/#website`;

const LOCALE_TO_BCP47 = {
  cs: 'cs-CZ',
  en: 'en-US',
  pl: 'pl-PL'
};

const BREADCRUMB_HOME_LABEL = {
  cs: 'Domů',
  en: 'Home',
  pl: 'Strona główna'
};

export const buildOrganizationSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'Organization',
  '@id': ORG_ID,
  name: SITE_NAME,
  url: SITE_URL,
  logo: `${SITE_URL}/web-app-manifest-512x512.png`,
  sameAs: [
    'https://www.facebook.com/leadhub.marketing',
    'https://www.linkedin.com/company/42347102/',
    'https://www.instagram.com/leadhub.marketing/',
    'https://www.youtube.com/@leadhub2660'
  ],
  contactPoint: {
    '@type': 'ContactPoint',
    telephone: '+420228229263',
    email: 'podpora@leadhub.co',
    contactType: 'customer support',
    availableLanguage: ['Czech', 'Polish']
  }
});

export const buildWebSiteSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  '@id': WEBSITE_ID,
  name: SITE_NAME,
  url: SITE_URL,
  inLanguage: ['cs-CZ', 'pl-PL'],
  publisher: { '@id': ORG_ID }
});

export const buildWebPageSchema = ({ title, description, canonicalUrl, locale }) => ({
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  '@id': `${canonicalUrl}#webpage`,
  name: title,
  description,
  url: canonicalUrl,
  inLanguage: LOCALE_TO_BCP47[locale] || LOCALE_TO_BCP47.cs,
  isPartOf: { '@id': WEBSITE_ID }
});

/**
 * Build BreadcrumbList from URL path segments.
 * Examples:
 *   / → [Home]
 *   /cenik → [Home, Ceník]
 *   /pl/cennik → [Home, Cennik]
 *
 * For glossary detail pages, pass `extraCrumbs` instead of relying on path.
 */
export const buildBreadcrumbListSchema = ({ path, locale, title, extraCrumbs = null }) => {
  const homeLabel = BREADCRUMB_HOME_LABEL[locale] || BREADCRUMB_HOME_LABEL.cs;
  const homeUrl = locale === 'cs' ? SITE_URL : `${SITE_URL}/${locale}`;

  const items = [{ name: homeLabel, url: homeUrl }];

  if (extraCrumbs) {
    items.push(...extraCrumbs);
  } else if (path && path !== '/') {
    const cleanPath = path.startsWith('/') ? path : `/${path}`;
    const fullUrl = locale === 'cs' ? `${SITE_URL}${cleanPath}` : `${SITE_URL}/${locale}${cleanPath}`;
    // Strip "| SiteName" suffix from <title> so the breadcrumb shows just the page name
    const cleanTitle = (title || '').split('|')[0].trim() || title;
    items.push({ name: cleanTitle, url: fullUrl });
  }

  if (items.length < 2) return null;

  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: item.url
    }))
  };
};

/**
 * Build FAQPage from a list of {question, answer} pairs.
 * Returns null if list is empty.
 */
export const buildFaqPageSchema = (questions) => {
  if (!questions || questions.length === 0) return null;

  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: questions.map((q) => ({
      '@type': 'Question',
      name: q.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: stripMarkdown(q.answer || '')
      }
    }))
  };
};

/**
 * Build Article schema for glossary detail pages.
 */
export const buildArticleSchema = ({ title, description, canonicalUrl, locale, publishedAt, updatedAt }) => ({
  '@context': 'https://schema.org',
  '@type': 'Article',
  headline: title,
  description,
  url: canonicalUrl,
  inLanguage: LOCALE_TO_BCP47[locale] || LOCALE_TO_BCP47.cs,
  ...(publishedAt && { datePublished: publishedAt }),
  ...(updatedAt && { dateModified: updatedAt }),
  publisher: { '@id': ORG_ID },
  mainEntityOfPage: { '@id': `${canonicalUrl}#webpage` }
});

/**
 * Strip Markdown/HTML to plain text so JSON-LD answer fields stay clean.
 * FAQPage spec allows simple HTML in answers, but plain text is safest for ranking.
 */
function stripMarkdown(text) {
  if (typeof text !== 'string') return '';
  return text
    .replace(/<[^>]+>/g, ' ')
    .replace(/!\[[^\]]*\]\([^)]+\)/g, '')
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    .replace(/[*_~`#>]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}
